import { logger } from "../../utils/logger";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Save, Home } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomepageManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    hero_title: "",
    hero_subtitle: "",
    hero_description: "",
    featured_event_id: "",
    countdown_event_id: "",
  });
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    const fetchHomepage = async () => {
      try {
        const response = await axios.get(`${API}/homepage`);
        setFormData({
          hero_title: response.data.hero_title || "",
          hero_subtitle: response.data.hero_subtitle || "",
          hero_description: response.data.hero_description || "",
          featured_event_id: response.data.featured_event_id || "",
          countdown_event_id: response.data.countdown_event_id || "",
        });
      } catch (err) {
        logger.error("Error fetching homepage:", err);
        toast.error("Failed to load homepage settings");
      } finally {
        setLoading(false);
      }
    };
    fetchHomepage();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/homepage`, formData, getAuthHeaders());
      toast.success("Homepage updated!");
    } catch (err) {
      toast.error("Failed to update homepage");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gold animate-pulse">Loading homepage settings...</div>;
  }

  return (
    <div data-testid="homepage-manager">
      <div className="mb-8">
        <h1 className="font-heading text-3xl text-white mb-2">Homepage Manager</h1>
        <p className="text-white/60">Customize your homepage hero section and featured content</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8" data-testid="homepage-form">
        {/* Hero Section */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <Home className="w-5 h-5 text-gold" />
            <h2 className="font-heading text-xl text-white">Hero Section</h2>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-white/80">Hero Title</Label>
              <Input
                value={formData.hero_title}
                onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="San Marcos Social Club"
                data-testid="hero-title-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Hero Subtitle</Label>
              <Input
                value={formData.hero_subtitle}
                onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1"
                placeholder="The Premier Poker Room in San Marcos"
                data-testid="hero-subtitle-input"
              />
            </div>

            <div>
              <Label className="text-white/80">Hero Description</Label>
              <Textarea
                value={formData.hero_description}
                onChange={(e) => setFormData({ ...formData, hero_description: e.target.value })}
                className="bg-casino-secondary border-white/10 text-white mt-1 min-h-[100px]"
                placeholder="Describe your poker room..."
                data-testid="hero-description-input"
              />
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="glass-card p-6">
          <h2 className="font-heading text-xl text-white mb-4">Preview</h2>
          <div className="bg-black/50 rounded-lg p-8 text-center">
            <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">
              Veteran Owned & Operated
            </p>
            <h3 className="font-heading text-3xl text-white mb-2">
              {formData.hero_title || "San Marcos Social Club"}
            </h3>
            <p className="text-gold text-lg mb-4">
              {formData.hero_subtitle || "The Premier Poker Room"}
            </p>
            <p className="text-white/60 text-sm max-w-md mx-auto">
              {formData.hero_description || "Your description here..."}
            </p>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={saving}
            className="bg-gold text-black hover:bg-gold-dark px-8"
            data-testid="save-homepage-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </div>
  );
}
